import type { VideoChannelIdentity, VideoVerificationView } from "@shared/videoVerification";
import { useEffect, useMemo, useState } from "react";

type RequestState = {
  key: string;
  status: "loading" | "available" | "unavailable";
  view: VideoVerificationView | null;
};

export interface VideoVerificationResult {
  status: "loading" | "available" | "unavailable";
  view: VideoVerificationView | null;
}

/** Fetch the no-store projection and never expose a response from another mode/date/channel. */
export function useVideoVerification(
  asOf: string | null,
  channelId: VideoChannelIdentity | null,
): VideoVerificationResult {
  const requestKey = useMemo(
    () => `${asOf === null ? "live" : `historical:${asOf}`}|${channelId ?? "all"}`,
    [asOf, channelId],
  );
  const [state, setState] = useState<RequestState | null>(null);

  useEffect(() => {
    let disposed = false;
    let controller: AbortController | null = null;
    let expiryTimer: number | null = null;

    const load = () => {
      controller?.abort();
      if (expiryTimer !== null) window.clearTimeout(expiryTimer);
      controller = new AbortController();
      const requestController = controller;
      const url = asOf === null
        ? "/api/video/channels"
        : `/api/video/channels?asOf=${encodeURIComponent(asOf)}`;
      const requestStartedAt = performance.now();
      setState({ key: requestKey, status: "loading", view: null });

      void fetch(url, { signal: requestController.signal, cache: "no-store" })
        .then(async (response) => {
          if (!response.ok) throw new Error("Video verification request failed");
          return await response.json() as VideoVerificationView;
        })
        .then((view) => {
          const viewClockMs = Date.parse(view?.asOf ?? "");
          const modeMatches = view?.mode === (asOf === null ? "live" : "historical");
          const boundaryMatches = asOf === null || Date.parse(view?.asOf ?? "") === Date.parse(asOf);
          if (view?.schemaVersion !== 1 || view.historicalMediaRetained !== false || !Number.isFinite(viewClockMs) || !modeMatches || !boundaryMatches || !Array.isArray(view?.channels)) {
            throw new Error("Video verification response did not match the requested view");
          }
          if (disposed || requestController.signal.aborted) return;
          const requestElapsedMs = Math.max(0, performance.now() - requestStartedAt);
          const clientEstimatedServerTime = viewClockMs + requestElapsedMs;
          let expiryCrossedInFlight = false;
          const safeView = asOf === null
            ? {
                ...view,
                channels: view.channels.map((record) => {
                  const playback = record.playback;
                  const expiresAtMs = playback.expiresAt ? Date.parse(playback.expiresAt) : Number.NaN;
                  const hasPriorResult = playback.state === "verified-playing" || playback.state === "verified-not-playing";
                  if (
                    !playback.expired && hasPriorResult && Number.isFinite(expiresAtMs)
                    && expiresAtMs <= clientEstimatedServerTime
                  ) {
                    expiryCrossedInFlight = true;
                    return { ...record, playback: { ...playback, state: "unknown" as const } };
                  }
                  return record;
                }),
              }
            : view;
          setState({ key: requestKey, status: "available", view: safeView });

          if (asOf === null) {
            const expiries = safeView.channels
              .filter((record) => !record.playback.expired)
              .map((record) => record.playback.expiresAt ? Date.parse(record.playback.expiresAt) : Number.NaN)
              .filter((time) => Number.isFinite(time) && time > clientEstimatedServerTime);
            if (expiries.length > 0) {
              expiryTimer = window.setTimeout(load, Math.min(...expiries) - clientEstimatedServerTime);
            } else if (expiryCrossedInFlight) {
              expiryTimer = window.setTimeout(load, 1_000);
            }
          }
        })
        .catch(() => {
          if (!disposed && !requestController.signal.aborted) setState({ key: requestKey, status: "unavailable", view: null });
        });
    };

    const refreshLive = () => { if (asOf === null) load(); };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshLive();
    };

    void load();
    window.addEventListener("focus", refreshLive);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      disposed = true;
      controller?.abort();
      if (expiryTimer !== null) window.clearTimeout(expiryTimer);
      window.removeEventListener("focus", refreshLive);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [asOf, requestKey]);

  if (state?.key !== requestKey) return { status: "loading", view: null };
  return { status: state.status, view: state.view };
}
